import { gql } from "@apollo/client/core";
import { additionalProductFragment } from "../query";
import { globalPropertyStateAspectFragment } from "../GlobalPropertyStateManager/query";

const productFields = (filterWorkflows: boolean) => gql`
    ${additionalProductFragment}
    fragment ProductFields on Product {
        id
        basePrice
        enabled
        imageUrl
        minimumOrderQuantity
        integrationProducts {
            id
            externalProductId
            externalVariantId
            additionalExternalProductId
            additionalExternalVariantId
            additionalIntegrationProduct {
                ...AdditionalIntegrationProductFields
            }
            integration {
                id
                enabled
                externalIntegrationId
                type
                isCurrent
            }
        }
        modelUrl
        name
        description
        overlayImageUrl
        preloadImageUrl
        sku
        skuCode
        workflows${filterWorkflows ? "(filters: $productWorkflowFilters)" : ""} {
            id
            index
            friendlyName
            isPresent
            workflowName
            imageUrl
        }
        productTags {
            id
            name
        }
    }
`;

const productCollectionProductFieldsFragment = (filterWorkflows: boolean) => gql`
    ${productFields(filterWorkflows)}
    fragment ProductCollectionProductFields on ProductCollectionProduct {
        id
        productId
        workflowId
        product {
            ...ProductFields
        }
    }
`;

export const productCollectionFieldsFragment = (eagerFetchProducts: boolean, filterWorkflows: boolean = false) => gql`
    ${eagerFetchProducts ? productCollectionProductFieldsFragment(filterWorkflows) : ""}
    fragment ProductCollectionFields on ProductCollection {
        id
        name
        description
        dispatchStartDate
        dispatchEndDate
        productCollectionCustomers {
            id
            customer {
                id
            }
        }
        image {
            key
            name
            fileLink
        }
        globalPropertyConfiguration {
            id
            aspects {
                name
                type
                title
                description
                entityId
                mandatory
                conditions {
                    targetAspectName
                    action
                    requiredVariantSelections
                }
                conditionMode
                data {
                    fileUpload {
                        colorPickerEnabled
                        pmsPickerEnabled
                        colorOptionId
                    }
                    text {
                        templatingEnabled
                    }
                }
            }
        }
        ${eagerFetchProducts ? "productCollectionProducts { ...ProductCollectionProductFields }" : ""}
        transformCollection {
            id
            name
            transforms {
                id
                name
                position {
                    x
                    y
                    z
                }
                rotation {
                    x
                    y
                    z
                }
                scale {
                    x
                    y
                    z
                }
            }
        }
    }
`;

// For small things that you would expect to always have
export const bundleBaseFieldsFragment = gql`
    fragment BundleBaseFields on Bundle {
        id
        dispatchDate
        purchaseOrder
        createdAt
        updatedAt
        name
        completed
        template
        templateStatus
        transactionsCount
        productsCount
        totalPriceSubunits
        quoteId
        orderId
        workflowViewerLink
        workflowViewerAmendLink
    }
`;

export const getBundleQuery = (eagerFetchProducts: boolean) => gql`
    ${globalPropertyStateAspectFragment}
    ${productCollectionFieldsFragment(eagerFetchProducts)}
    ${bundleBaseFieldsFragment}
    query GetBundle($id: String!) {
        globalPropertyState(bundleId: $id) {
            id
            aspects {
                ...GlobalPropertyStateAspectFields
            }
        }
        bundles(ids: [$id]) {
            ...BundleBaseFields
            bundleStateData
            metadata {
                key
                value
            }
            partner {
                id
            }
            productCollection {
                ...ProductCollectionFields
            }
            transactions {
                id
            }
            bundleShareActions {
                id
                title
                type
                stakeholderType
                precedence
                url
            }
        }
    }
`;

export const getBundleStakeholdersQuery = gql`
    query GetBundleStakeholders($id: String!) {
        bundles(ids: [$id]) {
            id
            dispatchDate
            purchaseOrder
            completed
            workflowViewerLink
            workflowViewerAmendLink
            currentBundleStakeholder {
                id
                authorizationStatus
                authorizationDate
                authorizationNote
                customer {
                    id
                    emailAddress
                    firstName
                    lastName
                    phoneNumber
                    partner {
                        id
                    }
                }
                type
            }
            bundleStakeholders {
                id
                authorizationStatus
                authorizationDate
                authorizationNote
                customer {
                    id
                    emailAddress
                    firstName
                    lastName
                    phoneNumber
                    partner {
                        id
                    }
                }
                type
            }
        }
    }
`;

export const getBundlesAndStakeholdersForCustomerQuery = gql`
    query GetBundlesForCustomer($id: String!) {
        customers(ids: [$id]) {
            bundleStakeholders {
                id
                authorizationStatus
                authorizationDate
                authorizationNote
                type
                bundle {
                    id
                    dispatchDate
                    purchaseOrder
                    completed
                    workflowViewerLink
                    workflowViewerAmendLink
                    name
                    createdAt
                    metadata {
                        key
                        value
                    }
                    partner {
                        id
                    }
                }
            }
        }
    }
`;

export const getProductCollectionProductsQuery = gql`
    ${productCollectionProductFieldsFragment(false)}
    query GetProductCollectionProducts($id: String!, $productIds: [String!]) {
        productCollections(ids: [$id], productIds: $productIds) {
            id
            productCollectionProducts {
                ...ProductCollectionProductFields
            }
        }
    }
`;

export const getProductCollectionProductsFilteredQuery = gql`
    query GetProductCollectionForFilteredProducts(
        $id: String!
        $filters: ProductCollectionProductFilterInput
        $tags: ProductCollectionProductTagFilterInput
        $sortKey: ProductCollectionProductSortKey
        $sortDescending: Boolean
    ) {
        productCollections(ids: [$id]) {
            id
            productCollectionProducts(
                filters: $filters
                tags: $tags
                sortKey: $sortKey
                sortDescending: $sortDescending
            ) {
                id
                product {
                    id
                }
            }
        }
    }
`;

export const getProductCollectionProductsFeedQuery = gql`
    ${productCollectionProductFieldsFragment(true)}
    query GetProductCollectionForProductsFeed(
        $id: String!
        $limit: Int
        $offset: Int
        $productFilters: ProductCollectionProductFilterInput
        $productWorkflowFilters: MetafieldFilterListInput
        $tags: ProductCollectionProductTagFilterInput
        $sortKey: ProductCollectionProductSortKey
        $sortDescending: Boolean
        $quickSearch: String
    ) {
        productCollections(ids: [$id]) {
            id
            productCollectionProductsFeed(
                filters: $productFilters
                workflowFilters: $productWorkflowFilters
                tags: $tags
                limit: $limit
                offset: $offset
                sortKey: $sortKey
                sortDescending: $sortDescending
                quickSearch: $quickSearch
            ) {
                items {
                    ...ProductCollectionProductFields
                }
                total
            }
        }
    }
`;

export const createBundleMutation = (eagerFetchProducts: boolean) => gql`
    ${productCollectionFieldsFragment(eagerFetchProducts)}
    mutation CreateBundle(
        $collectionId: String
        $initialMetadata: [MetadataInput!]
        $marketplaceThemeInstallId: String
        $marketplaceThemeInstallConfigurationId: String
    ) {
        bundleCreate(
            collectionId: $collectionId
            metadata: $initialMetadata
            marketplaceThemeInstallId: $marketplaceThemeInstallId
            marketplaceThemeInstallConfigurationId: $marketplaceThemeInstallConfigurationId
        ) {
            id
            bundleOwnerId
            metadata {
                key
                value
            }
            name
            partner {
                id
            }
            productCollection {
                ...ProductCollectionFields
            }
        }
    }
`;

export const duplicateBundleMutation = (eagerFetchProducts: boolean) => gql`
    ${globalPropertyStateAspectFragment}
    ${productCollectionFieldsFragment(eagerFetchProducts)}
    mutation DuplicateBundle(
        $id: String!
        $template: Boolean
        $marketplaceThemeInstallId: String
        $marketplaceThemeInstallConfigurationId: String
        $duplicateTransactions: Boolean
    ) {
        bundleDuplicate(
            id: $id
            template: $template
            marketplaceThemeInstallId: $marketplaceThemeInstallId
            marketplaceThemeInstallConfigurationId: $marketplaceThemeInstallConfigurationId
            duplicateTransactions: $duplicateTransactions
        ) {
            id
            bundleOwnerId
            template
            bundleStateData
            metadata {
                key
                value
            }
            name
            partner {
                id
            }
            productCollection {
                ...ProductCollectionFields
            }
            transactions {
                id
            }
            globalPropertyState {
                id
                aspects {
                    ...GlobalPropertyStateAspectFields
                }
            }
        }
    }
`;

export const updateBundleMutation = gql`
    mutation UpdateBundle(
        $id: String!
        $name: String
        $metadata: [MetadataInput!]
        $bundleStateData: String
        $dispatchDate: String
        $purchaseOrder: String
    ) {
        bundleUpdate(
            id: $id
            name: $name
            metadata: $metadata
            bundleStateData: $bundleStateData
            dispatchDate: $dispatchDate
            purchaseOrder: $purchaseOrder
        ) {
            id
        }
    }
`;

export const bundleAddTransactionMutation = gql`
    mutation BundleAddTransaction($id: String!, $transactionId: String!) {
        bundleAddTransaction(id: $id, transactionId: $transactionId) {
            id
        }
    }
`;

export const bundleAddTransactionsMutation = gql`
    mutation BundleAddTransactions($id: String!, $transactionIds: [String!]!, $transactionOwnerIds: [String!]) {
        bundleAddTransactions(id: $id, transactionIds: $transactionIds, transactionOwnerIds: $transactionOwnerIds) {
            id
        }
    }
`;

export const bundleRemoveTransactionMutation = gql`
    mutation BundleRemoveTransaction(
        $id: String!
        $transactionId: String!
        $name: String
        $metadata: [MetadataInput!]
        $bundleStateData: String
    ) {
        bundleRemoveTransaction(id: $id, transactionId: $transactionId) {
            id
        }
        bundleUpdate(id: $id, name: $name, metadata: $metadata, bundleStateData: $bundleStateData) {
            id
        }
    }
`;

export const bundleRemoveTransactionsMutation = gql`
    mutation BundleRemoveTransactions(
        $id: String!
        $transactionIds: [String!]!
        $name: String
        $metadata: [MetadataInput!]
        $bundleStateData: String
    ) {
        bundleRemoveTransactions(id: $id, transactionIds: $transactionIds) {
            id
        }
        bundleUpdate(id: $id, name: $name, metadata: $metadata, bundleStateData: $bundleStateData) {
            id
        }
    }
`;

export const bundleAddStakeholderMutation = gql`
    mutation BundleAddStakeholder($id: String!, $details: CustomerDetailsInput!, $type: String!) {
        bundleAddStakeholder(id: $id, details: $details, type: $type) {
            id
            bundleStakeholders {
                id
                customer {
                    id
                    emailAddress
                    firstName
                    lastName
                    phoneNumber
                    partner {
                        id
                    }
                }
                type
            }
        }
    }
`;

export const bundleRemoveStakeholderMutation = gql`
    mutation BundleAddStakeholder($id: String!, $emailAddress: String!) {
        bundleRemoveStakeholder(id: $id, emailAddress: $emailAddress) {
            id
        }
    }
`;

export const bundleUpdateStakeholdersMutation = gql`
    mutation BundleUpdateStakeholders($id: String!, $input: [BundleStakeholderInput!]!) {
        bundleUpdateStakeholders(id: $id, input: $input) {
            id
            bundleStakeholders {
                id
                customer {
                    id
                    emailAddress
                    firstName
                    lastName
                    phoneNumber
                    partner {
                        id
                    }
                }
                type
            }
        }
    }
`;

export const bundleUpdateTransactionOrderMutation = gql`
    mutation BundleUpdateTransactionOrder($id: String!, $transactionIds: [String!]!) {
        bundleUpdateTransactionOrder(id: $id, transactionIds: $transactionIds) {
            id
        }
    }
`;

export const bundleAssignProductCollectionMutation = (eagerFetchProducts: boolean) => gql`
    ${productCollectionFieldsFragment(eagerFetchProducts)}
    mutation BundleAssignProductCollection($id: String!, $productCollectionId: String!) {
        bundleAssignProductCollection(id: $id, collectionId: $productCollectionId) {
            id
            productCollection {
                ...ProductCollectionFields
            }
        }
    }
`;

export const bundleAssignGlobalPropertyConfiguration = gql`
    mutation AssignGlobalPropertyConfiguration($bundleId: String!, $globalPropertyConfigurationId: String!) {
        bundleUpdate(id: $bundleId, globalPropertyConfigurationId: $globalPropertyConfigurationId) {
            id
        }
    }
`;

export const customerBundlesFeedQuery = (includeMetadata: boolean) => gql`
    ${bundleBaseFieldsFragment}
    query CustomerBundlesFeed($id: String!, $limit: Int!, $offset: Int!, $template: Boolean, $templateStatus: BundleTemplateStatus, $ordered: Boolean) {
        customerBundlesFeed(id: $id, limit: $limit, offset: $offset, template: $template, templateStatus: $templateStatus, ordered: $ordered) {
            items {
                ...BundleBaseFields
                productCollection {
                    id
                    name
                    description
                    dispatchStartDate
                    dispatchEndDate
                    image {
                        key
                        name
                        fileLink
                    }
                    globalPropertyConfiguration {
                        id
                    }
                }
                ${includeMetadata ? "metadata { key, value }" : ""}
            }
            total
        }
    }
`;

export const finalizeUpdateBundleMutation = gql`
    mutation FinalizeUpdateBundle($bundleId: String!) {
        bundleFinalizeUpdate(bundleId: $bundleId)
    }
`;

export const bundleApprovalQuery = gql`
    mutation ApproveBundle($id: String!, $note: String, $stakeholderId: String) {
        bundleApprove(id: $id, note: $note, stakeholderId: $stakeholderId)
    }
`;

export const bundleRejectionQuery = gql`
    mutation RejectBundle($id: String!, $note: String, $stakeholderId: String) {
        bundleReject(id: $id, note: $note, stakeholderId: $stakeholderId)
    }
`;

export const transactionApprovalQuery = gql`
    mutation ApproveTransaction($id: String!, $note: String) {
        transactionApprove(id: $id, note: $note)
    }
`;

export const transactionRejectionQuery = gql`
    mutation RejectTransaction($id: String!, $note: String) {
        transactionReject(id: $id, note: $note)
    }
`;

export const createRecipientQuery = gql`
    mutation CreateRecipient(
        $firstName: String
        $lastName: String
        $address: String
        $suburb: String
        $state: String
        $email: String
        $postalCode: String
        $country: String
        $mobile: String
        $company: String
        $apartment: String
        $customField1: String
        $customField2: String
        $customField3: String
        $customField4: String
        $customField5: String
        $conversionConfigurationId: String
    ) {
        recipientCreate(
            firstName: $firstName
            lastName: $lastName
            address: $address
            suburb: $suburb
            state: $state
            email: $email
            postalCode: $postalCode
            country: $country
            mobile: $mobile
            company: $company
            apartment: $apartment
            customField1: $customField1
            customField2: $customField2
            customField3: $customField3
            customField4: $customField4
            customField5: $customField5
            conversionConfigurationId: $conversionConfigurationId
        ) {
            id
        }
    }
`;

export const updateRecipientQuery = gql`
    mutation UpdateRecipient(
        $id: String!
        $firstName: String
        $lastName: String
        $address: String
        $suburb: String
        $state: String
        $email: String
        $postalCode: String
        $country: String
        $mobile: String
        $company: String
        $apartment: String
        $customField1: String
        $customField2: String
        $customField3: String
        $customField4: String
        $customField5: String
        $conversionConfigurationId: String
    ) {
        recipientUpdate(
            id: $id
            firstName: $firstName
            lastName: $lastName
            address: $address
            suburb: $suburb
            state: $state
            email: $email
            postalCode: $postalCode
            country: $country
            mobile: $mobile
            company: $company
            apartment: $apartment
            customField1: $customField1
            customField2: $customField2
            customField3: $customField3
            customField4: $customField4
            customField5: $customField5
            conversionConfigurationId: $conversionConfigurationId
        ) {
            id
        }
    }
`;

export const transactionAttachRecipientQuery = gql`
    mutation AtttachRecipient($id: String!, $recipientId: String!) {
        transactionAttachRecipient(id: $id, recipientId: $recipientId) {
            id
        }
    }
`;

export const deleteBundleQuery = gql`
    mutation DeleteBundle($id: String!) {
        bundleDelete(id: $id)
    }
`;
